import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { spotsApi } from '../../api/client';
import { spotApiToSpot } from '../../context/DataContext';
import { getCountryFlagImageUrl, getCountryName } from '../../constants/countries';
import type { Spot } from '../../types';
import styles from './MostVoted.module.css';

type Category = 'liked' | 'beautiful' | 'cool' | 'sweetPhoto';

const CATEGORIES: { key: Category; label: string; icon: string }[] = [
  { key: 'liked', label: 'Most liked', icon: '♥' },
  { key: 'beautiful', label: 'Most beautiful', icon: '✨' },
  { key: 'cool', label: 'Most cool', icon: '😎' },
  { key: 'sweetPhoto', label: 'Sweet Photo', icon: '📷' },
];

export function MostVoted() {
  const [data, setData] = useState<{
    liked: Spot[];
    beautiful: Spot[];
    cool: Spot[];
    sweetPhoto: Spot[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<Category>('liked');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await spotsApi.mostVoted();
      setData({
        liked: res.liked.map(spotApiToSpot),
        beautiful: res.beautiful.map(spotApiToSpot),
        cool: res.cool.map(spotApiToSpot),
        sweetPhoto: res.sweetPhoto.map(spotApiToSpot),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load most voted spots');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const spots = data ? data[activeCategory] : [];

  return (
    <div className={styles.page}>
      <h1>Most Voted</h1>
      <p className={styles.subtitle}>
        Top 20 spots by likes, beautiful, cool, and sweet photo tags
      </p>

      <div className={styles.tabs}>
        {CATEGORIES.map(({ key, label, icon }) => (
          <button
            key={key}
            type="button"
            className={activeCategory === key ? styles.tabActive : styles.tab}
            onClick={() => setActiveCategory(key)}
          >
            {icon} {label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className={styles.loading}>Loading…</p>
      ) : error ? (
        <p className={styles.error}>{error}</p>
      ) : (
        <div className={styles.grid}>
          {spots.length === 0 ? (
            <p className={styles.empty}>No spots in this category yet.</p>
          ) : (
            spots.map((spot) => (
              <Link key={spot.id} to={`/spot/${spot.id}`} className={styles.thumb}>
                <div className={styles.thumbImageWrap}>
                  <img src={spot.imageUrl} alt="" />
                  {spot.countryCode && (
                    <span className={styles.thumbFlag}>
                      <img
                        src={getCountryFlagImageUrl(spot.countryCode)}
                        alt={getCountryName(spot.countryCode) || spot.countryCode}
                        title={getCountryName(spot.countryCode) || spot.countryCode}
                        className={styles.thumbFlagImg}
                      />
                    </span>
                  )}
                </div>
                <span className={styles.thumbLabel}>
                  {spot.car.make} {spot.car.model}
                </span>
                <span className={styles.thumbCounts}>
                  ♥ {(spot.likes ?? []).length} · ✨ {(spot.beautiful ?? []).length} · 😎 {(spot.cool ?? []).length} · 📷 {(spot.sweetPhoto ?? []).length}
                </span>
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
}
